import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

axios.defaults.withCredentials = true;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  // Live role refresh: role changes (e.g. Team → AC Member) apply within ~1 min without re-login.
  useEffect(() => {
    if (!user?.id) return;
    const refresh = async () => {
      try {
        const res = await axios.get(`${API}/auth/me`);
        setUser(prev => JSON.stringify(prev) === JSON.stringify(res.data) ? prev : res.data);
      } catch (err) {
        if (err.response?.status === 401) setUser(null);
      }
    };
    const iv = setInterval(() => { if (document.visibilityState === 'visible') refresh(); }, 60000);
    const onVis = () => { if (document.visibilityState === 'visible') refresh(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(iv); document.removeEventListener('visibilitychange', onVis); };
  }, [user?.id]);

  const login = useCallback(async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    return res.data;
  }, []);

  const register = useCallback(async (email, password, name) => {
    const res = await axios.post(`${API}/auth/register`, { email, password, name });
    return res.data;
  }, []);

  const verifyTotpSetup = useCallback(async (email, code, secret, mfaToken) => {
    const res = await axios.post(`${API}/auth/verify-totp-setup`, { email, code, secret, mfa_token: mfaToken });
    setUser(res.data.user);
    return res.data;
  }, []);

  const verifyTotp = useCallback(async (email, code, mfaToken) => {
    const res = await axios.post(`${API}/auth/verify-totp`, { email, code, mfa_token: mfaToken });
    setUser(res.data.user);
    try {
      const meRes = await axios.get(`${API}/auth/me`);
      setUser(meRes.data);
    } catch {
      // Role fetch after TOTP may fail silently
    }
    return res.data;
  }, []);

  const logout = useCallback(async () => {
    try { await axios.post(`${API}/auth/logout`); } catch { /* logout may fail silently */ }
    setUser(null);
  }, []);

  const value = useMemo(() => ({
    user, loading, login, register, verifyTotpSetup, verifyTotp, logout,
    isAdmin: user?.role === 'admin',
    investorMode: !!user?.investor_mode,
    isAcMember: user?.role === 'ac_member',
  }), [user, loading, login, register, verifyTotpSetup, verifyTotp, logout]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
